import "./App.less";
import { useEffect, useContext } from "react";
import { ConfigProvider, Spin, App as AntdApp } from "antd";
import ar_EG from "antd/lib/locale/ar_EG";
import en_GB from "antd/lib/locale/en_GB";
import Login from "../Auth/Login/Login";
import Register from "../Auth/Register/Register";
import Dashboard from "../Dashboard/Dashboard";
import { AppContext } from "../../context/AppContextProvider";
import { Route, Routes, useNavigate, useLocation } from "react-router-dom";
import { getMyProfile } from "../../network/network";
import { useTranslation } from "react-i18next";
import { messaging as FCMMessaging } from "../../firebaseInit";
import { onMessage } from "firebase/messaging";
import NotificationWithIcon from "../../components/NotificationWithIcon";
import Forgot from "../Auth/Forgot/Forgot";
import RegisterConfirmed from "../Auth/Register/RegisterConfirmed";
import TransactionsInvoice from "../Content/Transactions/TransactionsInvoice";
import QRordering from "../Content/QRorders/QRordering";
import QRcart from "../Content/QRorders/QRcart";
import QRorderHistory from "../Content/QRorders/QRorderHistory";
import CustomerExternalDisplay from "../Content/ExternalDisplays/Customer/CustomerExternalDisplay";
import KitchenExternalDisplay from "../Content/ExternalDisplays/Kitchen/KitchenExternalDisplay";
import { setMessageApi, setNotificationApi } from "../../utils/antdMessage";
import { handleApiError } from "../../utils/errorHandler";
import { APP_ANTD_THEME, APP_COLORS } from "../../configs/theme";

const PUBLIC_PATHS = [
  "/transactions/invoice",
  "/qr/ordering",
  "/qr/cart",
  "/qr/history",
  "/cds",
  "/kds",
];
const AntdMessageBridge = ({ children }) => {
  const { message, notification } = AntdApp.useApp();

  useEffect(() => {
    setMessageApi(message);
    setNotificationApi(notification);

    return () => {
      setMessageApi(null);
      setNotificationApi(null);
    };
  }, [message, notification]);

  return children;
};

const App = () => {
  const {
    authUser,
    setAuthUser,
    fullPageLoading,
    setFullPageLoading,
    appConfig,
    displayWebView,
    setDisplayWebView,
  } = useContext(AppContext);

  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const viewType = query.get("view");
    if (viewType === "webview") setDisplayWebView(viewType);
  }, [location.search, setDisplayWebView]);

  useEffect(() => {
    const access_token = localStorage.getItem("access_token");
    const path = location.pathname;

    const isPublic = PUBLIC_PATHS.some((p) => path.startsWith(p));
    if (isPublic) return;

    if (access_token && !authUser) {
      setFullPageLoading(true);

      getMyProfile()
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            localStorage.removeItem("access_token");
            navigate("/login", { replace: true });
            return;
          }

          const user = response?.data?.data?.user || null;
          setAuthUser(user);

          if (!user) {
            localStorage.removeItem("access_token");
            navigate("/login", { replace: true });
            return;
          }

          if (user.is_onboarding_complete === 0) {
            // Render gate in JSX below handles showing RegisterConfirmed.
            // No navigate needed — just return and let the gate do its job.
            return;
          }

          if (
            path === "/" ||
            path === "/login" ||
            path === "/register" ||
            path === "/forgot"
          ) {
            const lastVisitedRaw = localStorage.getItem("last_visited_page");
            if (lastVisitedRaw) {
              try {
                const last = JSON.parse(lastVisitedRaw);
                if (last?.path) {
                  navigate(last.path, { replace: true });
                  return;
                }
              } catch {}
            }
            navigate("/home", { replace: true });
          }
        })
        .catch((error) => {
          handleApiError(error, t);
          localStorage.removeItem("access_token");
          navigate("/login", { replace: true });
        })
        .finally(() => setFullPageLoading(false));

      return;
    }

    if (!access_token) {
      if (path === "/register" && displayWebView) {
        const params = new URLSearchParams();
        params.append("view", displayWebView);
        navigate(
          { pathname: "/register", search: "?" + params.toString() },
          { replace: true },
        );
        return;
      }

      if (path !== "/login" && path !== "/register" && path !== "/forgot") {
        navigate("/login", { replace: true });
      }
    }
  }, [
    authUser,
    setAuthUser,
    setFullPageLoading,
    displayWebView,
    location.pathname,
    navigate,
    t,
  ]);

  // Switch language. QR pages own their own language (en/ar/fr/es via
  // localStorage), so don't override it on /qr routes — only the portal is
  // driven from direction here.
  useEffect(() => {
    if (location.pathname.startsWith("/qr")) return;
    if (appConfig.direction === "rtl") i18n.changeLanguage("ar-EG");
    else i18n.changeLanguage("en-GB");
  }, [appConfig.direction, i18n, location.pathname]);

  // FCM listener
  useEffect(() => {
    const unsubscribe = onMessage(FCMMessaging, (payload) => {
      if (payload?.notification?.body === "RELOAD") window.location.reload();
      else if (payload?.notification?.title && payload?.notification?.body) {
        NotificationWithIcon(
          "info",
          payload.notification.title,
          payload.notification.body,
        );
      }
    });

    return () => typeof unsubscribe === "function" && unsubscribe();
  }, []);

  return (
    <ConfigProvider
      direction={appConfig.direction}
      locale={appConfig.direction === "ltr" ? en_GB : ar_EG}
      theme={APP_ANTD_THEME}
    >
      <AntdApp>
        <AntdMessageBridge>
          {fullPageLoading && (
            <div
              style={{
                textAlign: "center",
                paddingTop: "45vh",
                height: "100vh",
                position: "fixed",
                left: 0,
                top: 0,
                width: "100vw",
                background: APP_COLORS.white,
                zIndex: 999999,
              }}
            >
              <Spin size="large" />
            </div>
          )}

          {!fullPageLoading && !authUser ? (
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot" element={<Forgot />} />

              {/* PUBLIC */}
              <Route
                path="/transactions/invoice"
                element={<TransactionsInvoice />}
              />
              <Route path="/qr/ordering" element={<QRordering />} />
              <Route path="/qr/cart" element={<QRcart />} />
              <Route path="/qr/history" element={<QRorderHistory />} />
              <Route path="/cds" element={<CustomerExternalDisplay />} />
              <Route path="/kds" element={<KitchenExternalDisplay />} />
            </Routes>
          ) : !fullPageLoading &&
            authUser &&
            authUser.is_onboarding_complete === 0 ? (
            <RegisterConfirmed />
          ) : (
            <Dashboard />
          )}
        </AntdMessageBridge>
      </AntdApp>
    </ConfigProvider>
  );
};

export default App;
